import React, { useState, useEffect, useRef } from 'react';

const DocumentViewer = ({ document, onClose, client, channelName, userType }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [laserPointer, setLaserPointer] = useState(null);
  const [remotePointer, setRemotePointer] = useState({ x: 0, y: 0, visible: false });
  const [drawingMode, setDrawingMode] = useState('highlighter'); // ✅ Mode surligneur par défaut
  const [drawingColor, setDrawingColor] = useState(userType === 'prof' ? '#ff0000' : '#2563eb');
  const canvasRef = useRef(null);
  const documentRef = useRef(null);
  const laserTimeoutRef = useRef(null);
  const laserThrottleRef = useRef(null);

  useEffect(() => {
    console.log('🔗 Ajout des listeners documents pour:', userType);
    
    // Throttle function pour le laser
    laserThrottleRef.current = (e) => {
      if (drawingMode !== 'laser' || userType !== 'prof') return;
      
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = zoom / 100;
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      
      const laserPosition = { x, y, page: currentPage };
      setLaserPointer(laserPosition);
      broadcastLaserPointer(laserPosition);
    };
    
    // NOUVEAU: Écouter les événements personnalisés au lieu de stream-message directement
    const handleDocumentMessage = (event) => {
      const message = event.detail;
      console.log('📨 Message document reçu:', message.type);
      
      try {
        // Annotations synchronisées
        if (message.type === 'annotation') {
          const ann = message.annotation;
          setDrawings(prev => {
            const existing = prev.find(d => d.id === ann.id);
            if (existing) {
              return prev.map(d => d.id === ann.id ? { ...d, points: [...d.points, ...ann.points] } : d);
            }
            return [...prev, ann];
          });
        } else if (message.type === 'clear_annotations' && message.fromRemote) {
          setDrawings([]);
        } else if (message.type === 'page_sync' && userType !== 'prof') {
          // Synchronisation de page ET zoom pour les élèves
          console.log('🎓 Synchronisation page reçue - élève passe à la page:', message.page);
          setCurrentPage(message.page);
          if (message.zoom) {
            console.log('🔍 Synchronisation zoom reçue:', message.zoom);
            setZoom(message.zoom);
          }
        } else if (message.type === 'zoom_sync' && userType !== 'prof') {
          // Synchronisation zoom seul
          console.log('🔍 Synchronisation zoom seule reçue:', message.zoom);
          setZoom(message.zoom);
        } else if (message.type === 'clear_annotations') {
          console.log('🧹 Effacement annotations reçu côté distant');
          setDrawings([]);
        } else if (message.type === 'audio_control' && userType !== 'prof') {
          // RESTAURÉ: Synchronisation des contrôles audio pour les élèves
          handleAudioSync(message.controls);
        } else if (message.type === 'scroll_sync' && userType !== 'prof') {
          // Synchronisation du scroll PDF
          console.log('Scroll sync reçu:', message.scrollY);
          const iframe = window.document.querySelector('iframe');
          if (iframe && iframe.contentWindow) {
            try {
              iframe.contentWindow.scrollTo(0, message.scrollY);
            } catch (error) {
              console.log('Scroll sync impossible:', error);
            }
          }
        }
      } catch (error) {
        console.error('Erreur traitement message document:', error);
      }
    };

    // Écouter les événements personnalisés redistribués par CoursVideo.js
    window.addEventListener('agora-document-message', handleDocumentMessage);
    console.log('✅ Listeners événements documents ajoutés');

    return () => {
      window.removeEventListener('agora-document-message', handleDocumentMessage);
      console.log('🧹 Listeners événements documents supprimés');
      
      if (laserTimeoutRef.current) {
        clearTimeout(laserTimeoutRef.current);
      }
    };
  }, [userType, drawingMode, zoom, currentPage]);

   const handleAudioSync = (controls) => {
    // CORRIGÉ: Définir documentId avec des tirets au lieu de points
    const documentId = String(document.id).replace(/\./g, '-');
    const audioElement = window.document.querySelector('#audio-player-' + documentId);
    if (audioElement) {
      console.log('🎵 Synchronisation audio côté élève:', controls);
      
      if (controls.action === 'play') {
        // ✅ CORRECTION: Définir currentTime AVANT play avec délai
        if (controls.currentTime !== undefined) {
          audioElement.currentTime = controls.currentTime;
          console.log('⏱️ Audio currentTime défini à:', controls.currentTime);
        }
        
        // Attendre 50ms pour que currentTime soit bien appliqué
        setTimeout(() => {
          audioElement.play().catch(console.error);
        }, 50);
        
      } else if (controls.action === 'pause') {
        audioElement.pause();
      } else if (controls.action === 'seek') {
        audioElement.currentTime = controls.currentTime;
      } else if (controls.action === 'speed_change') {
        audioElement.playbackRate = controls.playbackSpeed;
      }
      // ✅ CORRECTION: Ajout gestion timeupdate pour sync continue
      else if (controls.action === 'timeupdate') {
        // Synchronisation continue pour éviter le décalage
        if (!audioElement.paused) {  // ✅ AJOUTÉ: Seulement si audio joue
          const drift = Math.abs(audioElement.currentTime - controls.currentTime);
          if (drift > 2) {  // Si décalage > 2 secondes, resync
            console.log('🔄 Resync audio - décalage:', drift, 'secondes');
            audioElement.currentTime = controls.currentTime;
          }
        }
      }

    } else {
      console.log('🔍 Élément audio non trouvé pour ID:', documentId);
    }
  };


	// ✅ CORRECTION 6: Fix canvas resize
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const scale = zoom / 100;
      canvas.width = 800 * scale;
      canvas.height = 1130 * scale;
      renderDrawings();
    }
  }, [zoom, currentPage, drawings]);

  const startDrawing = (e) => {
    if (drawingMode === 'pointer') return;
    
    console.log('🎨 Début du dessin - Mode:', drawingMode);
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoom / 100;
    
    // Coordonnées corrigées avec micro-ajustement pour le décalage résiduel
    const offsetX = userType === 'prof' ? 2 : 0; // Ajustement fin côté prof
    const offsetY = userType === 'prof' ? 2 : 0;
    
    const x = Math.round((e.clientX - rect.left - offsetX) / scale * 10) / 10;
    const y = Math.round((e.clientY - rect.top - offsetY) / scale * 10) / 10;
    
    const newPath = {
      id: Date.now() + Math.random(),
      page: currentPage,
      type: drawingMode,
      color: drawingColor,
      points: [{ x, y }],
      timestamp: Date.now()
    };
    
    console.log('🎨 Nouveau path créé:', newPath);
    setDrawings(prev => [...prev, newPath]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoom / 100;
    
    const offsetX = userType === 'prof' ? 2 : 0;
    const offsetY = userType === 'prof' ? 2 : 0;
    
    const x = Math.round((e.clientX - rect.left - offsetX) / scale * 10) / 10;
    const y = Math.round((e.clientY - rect.top - offsetY) / scale * 10) / 10;
    
    // Dessiner immédiatement le trait au lieu d'attendre le rendu complet
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.strokeStyle = drawingColor;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.globalAlpha = 1.0;
    
    setDrawings(prev => {
      const newDrawings = [...prev];
      const lastPath = newDrawings[newDrawings.length - 1];
      if (lastPath) {
        lastPath.points.push({ x, y });
      }
      return newDrawings;
    });
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    console.log('🎨 Fin du dessin');
    setIsDrawing(false);
    
    // Envoyer l'annotation à tous les participants
    const lastAnnotation = drawings[drawings.length - 1];
    console.log('🎨 Tentative envoi annotation:', lastAnnotation);
    
    if (lastAnnotation && lastAnnotation.points.length > 0) {
      console.log('✅ Envoi annotation avec', lastAnnotation.points.length, 'points');
      broadcastAnnotation(lastAnnotation);
    } else {
      console.log('❌ Pas d\'annotation à envoyer ou pas de points');
    }
  };

  const broadcastAnnotation = async (annotation) => {
    if (!client) return;
    try {
      const CHUNK = 20;
      const points = annotation.points;
      for (let i = 0; i < points.length; i += CHUNK) {
        const chunk = { type: 'annotation', annotation: { ...annotation, points: points.slice(i, i + CHUNK), chunkStart: i }};
        await client.sendStreamMessage(new TextEncoder().encode(JSON.stringify(chunk)));
      }
      console.log('✅ Annotation envoyée en', Math.ceil(points.length/CHUNK), 'morceaux');
    } catch(e) { console.warn('⚠️ Annotation non envoyée:', e.message); }
  };

  const broadcastLaserPointer = async (position) => {
    // DÉSACTIVÉ temporairement - causait des erreurs
    console.log('🔴 Laser local uniquement:', position);
    
    /* DÉSACTIVÉ temporairement - causait des erreurs
    try {
      const message = {
        type: 'laser_pointer',
        position: position,
        timestamp: Date.now()
      };
      
      console.log('📤 Envoi laser:', message);
      const encoder = new TextEncoder();
      await client.sendStreamMessage(encoder.encode(JSON.stringify(message)));
    } catch (error) {
      console.error('❌ Erreur envoi laser:', error);
    }
    */
  };

  const broadcastAudioControl = async (controls) => {
    if (userType !== 'prof') return;
    
    try {
      const message = {
        type: 'audio_control',
        controls: controls,
        documentId: document.id,
        timestamp: Date.now()
      };
      
      console.log('🎵 Envoi contrôle audio côté prof:', controls);
      const encoder = new TextEncoder();
      await client.sendStreamMessage(encoder.encode(JSON.stringify(message)));
      console.log('✅ Contrôle audio envoyé avec succès');
    } catch (error) {
      console.error('❌ Erreur envoi contrôle audio:', error);
    }
  };

  const changePage = async (newPage) => {
    console.log('=== DEBUG CHANGEPAGE ===');
    console.log('newPage:', newPage);
    console.log('userType:', userType);
    console.log('client existe:', !!client);
    console.log('client connectionState:', client?.connectionState);
    
    setCurrentPage(newPage);
    
    // Si prof, synchroniser la page ET le zoom pour tous
    if (userType === 'prof') {
      console.log('👨‍🏫 Mode prof - tentative synchronisation page + zoom');
      
      if (!client) {
        console.error('❌ Client Agora non disponible');
        return;
      }
      
      try {
        const message = {
          type: 'page_sync',
          page: newPage,
          zoom: zoom, // Synchroniser aussi le zoom
          documentId: document.id,
          timestamp: Date.now()
        };
        
        console.log('📤 Message à envoyer:', message);
        
        const encoder = new TextEncoder();
        const result = await client.sendStreamMessage(encoder.encode(JSON.stringify(message)));
        
        console.log('✅ Message envoyé avec succès:', result);
      } catch (error) {
        console.error('❌ Erreur envoi message:', error);
        console.error('Error details:', error.message);
      }
    } else {
      console.log('👨‍🎓 Mode élève - pas de synchronisation à envoyer');
    }
  };

  const changeZoom = async (newZoom) => {
    setZoom(newZoom);
    
    // Si prof, synchroniser le zoom pour tous
    if (userType === 'prof' && client) {
      try {
        const message = {
          type: 'zoom_sync',
          zoom: newZoom,
          documentId: document.id,
          timestamp: Date.now()
        };
        
        console.log('🔍 Synchronisation zoom:', newZoom);
        const encoder = new TextEncoder();
        await client.sendStreamMessage(encoder.encode(JSON.stringify(message)));
      } catch (error) {
        console.error('Erreur sync zoom:', error);
      }
    }
  };

  const clearAnnotations = async () => {
    
    console.log('🧹 Effacement des annotations');
    setDrawings([]);
    
    // DÉSACTIVÉ - Pas de synchronisation de l'effacement pour éviter erreurs
    console.log('🧹 Effacement local uniquement');
    
    /* DÉSACTIVÉ temporairement - causait des erreurs
    try {
      const message = {
        type: 'clear_annotations',
        documentId: document.id,
        timestamp: Date.now()
      };
      
      console.log('📤 Envoi message effacement');
      const encoder = new TextEncoder();
      await client.sendStreamMessage(encoder.encode(JSON.stringify(message)));
      console.log('✅ Message effacement envoyé');
    } catch (error) {
      console.error('❌ Erreur effacer annotations:', error);
    }
    */
  };

  const renderDrawings = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const scale = zoom / 100;
    
    // Dessiner les annotations pour la page courante
    const currentPageDrawings = drawings.filter(drawing => drawing.page === currentPage);
    // Forcer la persistance des annotations par page
    if (currentPageDrawings.length === 0 && drawings.length > 0) {
      console.log('Annotations perdues pour page', currentPage, '- tentative récupération');
      // Ne pas effacer le canvas si on a des annotations pour d'autres pages
      return;
    }
    console.log('🎨 Rendu de', currentPageDrawings.length, 'annotations pour la page', currentPage);
    
    currentPageDrawings.forEach(drawing => {
      if (drawing.points.length < 1) return;
      
      ctx.save(); // Sauvegarder l'état du contexte
      ctx.beginPath();
      ctx.strokeStyle = drawing.color;
      ctx.globalAlpha = drawing.type === 'highlighter' ? 0.6 : 1.0; // Alpha plus visible
      ctx.lineWidth = drawing.type === 'highlighter' ? 12 : 3; // Ligne plus épaisse
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      if (drawing.points.length === 1) {
        // Point unique (clic rapide)
        ctx.arc(drawing.points[0].x * scale, drawing.points[0].y * scale, 2, 0, 2 * Math.PI);
        ctx.fill();
      } else {
        // Ligne
        ctx.moveTo(drawing.points[0].x * scale, drawing.points[0].y * scale);
        
        for (let i = 1; i < drawing.points.length; i++) {
          ctx.lineTo(drawing.points[i].x * scale, drawing.points[i].y * scale);
        }
        ctx.stroke();
      }
      
      ctx.restore(); // Restaurer l'état du contexte
    });
    
    // Dessiner le pointeur laser (local uniquement)
    if (laserPointer && laserPointer.page === currentPage) {
      ctx.save();
      ctx.fillStyle = '#ff0000';
      ctx.globalAlpha = 0.9;
      ctx.arc(
        laserPointer.x * scale, 
        laserPointer.y * scale, 
        10, 0, 2 * Math.PI
      );
      ctx.fill();
      
      // Effet de pulsation plus visible
      ctx.strokeStyle = '#ff4444';
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }
  };

  useEffect(() => {
    renderDrawings();
  }, [drawings, currentPage, zoom, laserPointer]);

  const getFileType = () => {
    const fname = document?.fileName || document?.name || '';
    if (!fname) return 'pdf';
    return fname.toLowerCase().split('.').pop();
  };


  // Sync pointeur laser vers élève (throttle 50ms)
  const lastPointerSend = useRef(0);
  const isLaserPointer = drawingMode === 'laser';

  const handlePointerMove = React.useCallback((e) => {
    if (!client || !isLaserPointer || userType !== 'prof') return;
    const now = Date.now();
    if (now - lastPointerSend.current < 50) return;
    lastPointerSend.current = now;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
    const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
    client.sendStreamMessage(
      new TextEncoder().encode(JSON.stringify({ type: 'pointer_sync', x: parseFloat(x), y: parseFloat(y) }))
    ).catch(() => {});
  }, [client, isLaserPointer, userType]);


  // Réception pointeur côté élève
  useEffect(() => {
    if (userType === 'prof') return;
    const handlePointerMsg = (event) => {
      const msg = event.detail;
      if (msg.type === 'pointer_sync') {
        setRemotePointer({ x: msg.x, y: msg.y, visible: true });
        clearTimeout(window._pointerHideTimer);
        window._pointerHideTimer = setTimeout(() => {
          setRemotePointer(p => ({ ...p, visible: false }));
        }, 2000);
      }
    };
    window.addEventListener('agora-document-message', handlePointerMsg);
    return () => window.removeEventListener('agora-document-message', handlePointerMsg);
  }, [userType]);

  const isPDF = () => getFileType() === 'pdf';
  const isImage = () => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(getFileType());
  const isAudio = () => ['mp3', 'wav', 'ogg'].includes(getFileType());

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.95)',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>


      {/* Barre d'outils */}
      <div style={{
        backgroundColor: '#2d2d2d',
        padding: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h3 style={{ margin: 0 }}>{document.fileName}</h3>
          
          {/* Contrôles de page - PDF seulement */}
          {isPDF() && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => changePage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage <= 1 ? '#666' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Précédent
              </button>
              
              <span>Page {currentPage} / {totalPages}</span>
              
              <button
                onClick={() => changePage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage >= totalPages ? '#666' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Suivant →
              </button>
            </div>
          )}
          
          {/* Contrôles de zoom */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => changeZoom(Math.max(50, zoom - 25))}
              style={{
                padding: '8px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔍-
            </button>
            <span>{zoom}%</span>
            <button
              onClick={() => changeZoom(Math.min(200, zoom + 25))}
              style={{
                padding: '8px 12px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔍+
            </button>
          </div>
        </div>

        {/* Outils d'annotation - Professeur seulement - VERSION SIMPLIFIÉE */}
{(isPDF() || isImage()) && (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    {/* Pointeur */}
    <button
      onClick={() => setDrawingMode('pointer')}
      style={{
        padding: '8px 12px',
        backgroundColor: drawingMode === 'pointer' ? '#007bff' : '#666',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      👆 Pointeur
    </button>
    
    {/* Stylo */}
    <button
      onClick={() => setDrawingMode('pen')}
      style={{
        padding: '8px 12px',
        backgroundColor: drawingMode === 'pen' ? '#007bff' : '#666',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      ✏️ Stylo
    </button>
    
    {/* Sélecteur de couleur */}
    <input
      type="color"
      value={drawingColor}
      onChange={(e) => setDrawingColor(e.target.value)}
      style={{
        width: '40px',
        height: '35px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    />
    
    {/* Effacer */}
    <button
      onClick={clearAnnotations}
      style={{
        padding: '8px 12px',
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      🗑️ Effacer
    </button>
  </div>
)}

  <button
          onClick={onClose}
          style={{
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          ✕ Fermer
        </button>
      </div>
      {/* Zone de document avec canvas pour annotations */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        position: 'relative',
        overflow: 'auto'
      }}>
        <div style={{ position: 'relative' }} onMouseMove={handlePointerMove}>
          {/* Pointeur distant côté élève */}
          {userType !== 'prof' && remotePointer.visible && (
            <div style={{
              position: 'absolute',
              left: remotePointer.x + '%',
              top: remotePointer.y + '%',
              transform: 'translate(-50%, -50%)',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 0, 0, 0.7)',
              border: '2px solid white',
              zIndex: 9999,
              pointerEvents: 'none',
              boxShadow: '0 0 8px rgba(255,0,0,0.5)'
            }} />
          )}
          {/* Document */}
          <div
            ref={documentRef}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              maxWidth: '90vw',
            }}
          >
          {isPDF() && (
              <div style={{ 
                width: '800px', 
                height: '1132px',
                overflow: 'hidden', 
                position: 'relative',
                backgroundColor: 'white'
              }}>  
                
                <iframe
                  key={`pdf-viewer-${currentPage}`}
                 src={`${document.fileUrl}#page=${currentPage}&view=Fit&pagemode=none&toolbar=0&navpanes=0&scrollbar=0&statusbar=0`}                   
                  style={{
                    width: '100%',
                    height: '1132px',
                    border: 'none',
                    backgroundColor: 'white',
                    marginTop: '0',
                    pointerEvents: 'none'
                  }}
                  title={document.fileName}
                  onLoad={() => setTotalPages(200)}
                  scrolling="no"
                />
              </div>
            )}
  
            {isImage() && (
              <img
                src={document.fileUrl}
                alt={document.fileName}
                style={{
                  maxWidth: '800px',
                  maxHeight: '1000px',
                  backgroundColor: 'white',
                  border: '1px solid #ddd'
                }}
              />
            )}
            
            {isAudio() && (
              <div style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <h3>{document.fileName}</h3>
                <audio
                  id={'audio-player-' + String(document.id).replace(/\./g, '-')}
                  controls
                  src={document.fileUrl}
                  autoPlay={userType !== 'prof'}
                  style={{ width: '100%', marginTop: '20px' }}
                  onPlay={(e) => {
                    console.log('🎵 Audio PLAY côté prof, currentTime:', e.target.currentTime);
                    broadcastAudioControl({ 
                      action: 'play', 
                      currentTime: e.target.currentTime 
                    });
                  }}
                  onPause={(e) => {
                    console.log('🎵 Audio PAUSE côté prof, currentTime:', e.target.currentTime);
                    broadcastAudioControl({ 
                      action: 'pause', 
                      currentTime: e.target.currentTime 
                    });
                  }}
                  onSeeked={(e) => {
                    console.log('🎵 Audio SEEK côté prof, currentTime:', e.target.currentTime);
                    broadcastAudioControl({ 
                      action: 'seek', 
                      currentTime: e.target.currentTime 
                    });
                  }}
                  onTimeUpdate={(e) => {
                    // Synchronisation continue du temps (throttlée)
                    if (userType === 'prof' && !e.target.paused) {
                      clearTimeout(window.audioSyncTimeout);
                      window.audioSyncTimeout = setTimeout(() => {
                        broadcastAudioControl({ 
                          action: 'timeupdate', 
                          currentTime: e.target.currentTime 
                        });
                      }, 1000); // Sync toutes les secondes
                    }
                  }}
                >
                  Votre navigateur ne supporte pas la lecture audio.
                </audio>
                
                {userType === 'prof' && (
                  <div style={{ marginTop: '15px' }}>
                    <label style={{ color: '#333', marginRight: '10px' }}>Vitesse: </label>
                    <select 
                      defaultValue="1"
                      style={{ padding: '5px', borderRadius: '4px' }}
                      onChange={(e) => {
                        // CORRIGÉ: Utiliser des tirets au lieu d'échapper les points
                        const documentId = String(document.id).replace(/\./g, '-');
                        const audioElement = window.document.querySelector('#audio-player-' + documentId);
                        if (audioElement) {
                          audioElement.playbackRate = parseFloat(e.target.value);
                          console.log('🎵 Audio SPEED CHANGE côté prof:', e.target.value);
                          broadcastAudioControl({ 
                            action: 'speed_change', 
                            playbackSpeed: parseFloat(e.target.value) 
                          });
                        }
                      }}
                    >
                      <option value="0.5">0.5x</option>
                      <option value="0.75">0.75x</option>
                      <option value="1" defaultValue="1">1x</option>
                      <option value="1.25">1.25x</option>
                      <option value="1.5">1.5x</option>
                      <option value="2">2x</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Canvas pour annotations - PDF et images seulement */}
          {(isPDF() || isImage()) && (
            <canvas
              ref={canvasRef}
              width={800 * zoom / 100}
              height={1130 * zoom / 100}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                cursor: drawingMode === 'pointer' ? 'default' : 
                       drawingMode === 'laser' ? 'crosshair' : 'crosshair',
                pointerEvents: drawingMode === 'pointer' ? 'none' : 'auto'
              }}
              onMouseDown={startDrawing}
              onMouseMove={(e) => {
                draw(e);
                if (drawingMode === 'laser' && laserThrottleRef.current) {
                  laserThrottleRef.current(e);
                }
              }}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
